import { parsePath } from '../HistoryUtils';
export const getCurrentLocation = (windowRef) => {
    const location = windowRef ? windowRef.location : {};
    return {
        hash: location.hash || '',
        host: location.host || '',
        hostName: location.hostname || '',
        href: location.href || '',
        origin: location.origin || '',
        pathname: location.pathname || '',
        port: location.port || '',
        protocol: location.protocol || '',
        search: location.search || ''
    };
};
const isExtraneousPopstateEvent = (event) => {
    return event && event.state === undefined && navigator.userAgent.indexOf('CriOS') === -1;
};
export class BrowserHistory {
    constructor(windowRef) {
        this._navigationFilters = [];
        this._navigationListeners = [];
        this._restoringPosition = false;
        this._popStateHandler = (event) => this._handlePopState(event);
        this._window = windowRef;
        this._globalHistory = windowRef.history;
        windowRef.addEventListener('popstate', this._popStateHandler);
        const { historyStack, historyIndex } = BrowserHistory.getHistoryState(windowRef);
        if (historyStack && typeof historyIndex !== 'undefined') {
            this._historyStack = historyStack.slice(0);
            this._historyIndex = historyIndex;
            if (this._historyIndex === 0) {
                this._globalHistory.forward();
            }
        }
        else {
            this._initHistoryState();
        }
    }
    static getHistoryState(windowRef) {
        try {
            return windowRef.history.state || {};
        }
        catch (e) {
            return {};
        }
    }
    location() {
        return getCurrentLocation(this._window);
    }
    canGoBack() {
        return this._historyIndex > 1;
    }
    goBack() {
        return this._globalHistory.back();
    }
    goForward() {
        return this._globalHistory.forward();
    }
    push(path) {
        if (path === getCurrentLocation(this._window).pathname) {
            return this.replace(path);
        }
        if (!this._allowNavigation(path, "push")) {
            return;
        }
        const key = BrowserHistory._createKey();
        this._historyStack.splice(this._historyIndex + 1);
        this._historyStack.push(key);
        this._historyIndex += 1;
        this._globalHistory.pushState(this._getState(), '', path);
        this._notifyListeners(getCurrentLocation(this._window), "push");
    }
    replace(path) {
        if (!this._allowNavigation(path, "replace")) {
            return;
        }
        const key = BrowserHistory._createKey();
        this._historyStack[this._historyIndex] = key;
        this._globalHistory.replaceState(this._getState(), '', path);
        this._notifyListeners(getCurrentLocation(this._window), "replace");
    }
    listen(callback) {
        this._navigationListeners = this._navigationListeners || [];
        this._navigationListeners.push(callback);
        return () => this._removeNavigationListener(callback);
    }
    filter(filter) {
        this._navigationFilters = this._navigationFilters || [];
        this._navigationFilters.push(filter);
        return () => this._removeNavigationFilter(filter);
    }
    _removeNavigationFilter(filter) {
        if (!this._navigationFilters) {
            return;
        }
        const index = this._navigationFilters.indexOf(filter);
        if (index !== -1) {
            this._navigationFilters.splice(index, 1);
        }
    }
    static _createKey() {
        return Math.random().toString(36).substr(2, 8);
    }
    _getState() {
        return {
            historyStack: this._historyStack.slice(0),
            historyIndex: this._historyIndex
        };
    }
    _handlePopState(event) {
        if (isExtraneousPopstateEvent(event)) {
            return;
        }
        if (this._restoringPosition) {
            this._restoringPosition = false;
            return;
        }
        const location = getCurrentLocation(this._window);
        let navAction = "unknown";
        let foundIndex = -1;
        if (event && event.state) {
            const { historyStack, historyIndex } = event.state;
            if (historyStack && typeof historyIndex !== 'undefined') {
                const key = historyStack[historyIndex];
                foundIndex = this._historyStack.indexOf(key);
                if (foundIndex >= 0 && foundIndex < this._historyIndex) {
                    navAction = "back";
                }
                else if (foundIndex > this._historyIndex) {
                    navAction = "forward";
                }
            }
            if (!this._allowNavigation(location.pathname, navAction)) {
                this._rejectPop(location, navAction, foundIndex);
                return;
            }
            if (historyIndex === 0 && navAction === "back") {
                this._globalHistory.back();
            }
        }
        if (foundIndex === -1) {
            this._initHistoryState();
        }
        else {
            this._historyIndex = foundIndex;
            this._globalHistory.replaceState(this._getState(), '', location.href);
        }
        this._notifyListeners(location, navAction);
    }
    _rejectPop(location, action, popIndex) {
        if (popIndex === -1) {
            this._initHistoryState();
            this._notifyListeners(location, action);
        }
        else {
            const delta = this._historyIndex - popIndex;
            if (delta !== 0) {
                this._restoringPosition = true;
                this._globalHistory.go(delta);
            }
        }
        return;
    }
    _initHistoryState() {
        this._historyIndex = 0;
        this._historyStack = [BrowserHistory._createKey()];
        this._globalHistory.replaceState(this._getState(), '', this._window.location.href);
        this._historyStack.push(BrowserHistory._createKey());
        this._historyIndex = 1;
        this._globalHistory.pushState(this._getState(), '', this._window.location.href);
    }
    _allowNavigation(uri, action) {
        let block = false;
        const location = parsePath(uri);
        this._navigationFilters.forEach(filter => {
            block = filter(location, action) || block;
        });
        return !block;
    }
    _notifyListeners(location, action) {
        this._navigationListeners.forEach(listener => {
            listener(location, action);
        });
    }
    _removeNavigationListener(listener) {
        if (!this._navigationListeners) {
            return;
        }
        const index = this._navigationListeners.indexOf(listener);
        if (index !== -1) {
            this._navigationListeners.splice(index, 1);
        }
    }
}
export default BrowserHistory;



// WEBPACK FOOTER //
// ./node_modules/@ps/payscale-shared-ui/lib/BrowserHistory/index.js